import { ApolloError } from '@apollo/client';
import { AvailabilityConstraints } from '@/experiences/sales-portal/domain/ordering/blocs/loaders/use-sales-portal-available-order-fulfillments-loader';
import { EmployeeRole, GetAvailableOrderFulfillmentResponse } from '@/types';
import {
  OrderGuideCollectionData,
  useSalesPortalOrderGuideCollectionsLoader,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/sales-portal-order-guide-collections-loader';
import { RestaurantListData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/sales-portal-restaurant-transformer';
import { SalesRepConfig } from '@domain/configuration/types';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import { TimeZoneMap } from '@domain/fulfillment-settings/types/types';
import { useAuthenticatedUserUUID } from '@/contexts/auth-context';
import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useChatSalesPortalDynamicConfigValue } from '@/experiences/sales-portal/blocs/hooks/use-chat-dynamic-config';
import { useConfidentialVisibilityStore } from '@/experiences/sales-portal/domain/ordering/blocs/stores/order-entry-confidential-visibility-store';
import { useIsCommissionsEnabled } from '@/experiences/sales-portal/blocs/hooks/use-is-commissions-enabled';
import { useIsGrossProfitHidden } from '@/experiences/sales-portal/blocs/hooks/use-is-gross-profit-hidden';
import { useSalesPortalEmployeeRole } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-employee-role';
import { useSalesPortalFulfillments } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-sales-portal-fulfillments';
import { useSalesPortalSelectedRestaurantLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-sales-portal-selected-restaurant-loader';
import { useSalesRepConfig } from '@/experiences/sales-portal/blocs/hooks/use-sales-rep-config';
import type { OrderEntrySyncParams } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/sync/use-order-entry-sync';

interface UseOrderEntryMobileDataParams {
  restaurantUUID: string;
  supplierUUID: string;
}

export interface OrderEntryMobileLoaderArgs {
  chatUUID: string;
  deliveryDate: string;
  linkedSupplierCollectionUUID?: string;
  orderGuideUUID: string;
  restaurantTimezone: string;
  restaurantUUID: string;
  salesRepConfig: SalesRepConfig | null;
  savedSelectedFulfillment: NonNullable<OrderEntrySyncParams['fulfillment']>;
  supplierTimezone: string;
  supplierUUID: string;
}

export interface OrderEntryMobileData {
  activeOrderGuide: OrderGuideCollectionData | null;
  additionalOrderValidationRoles: EmployeeRole[];
  areSubstitutionsEnabled: boolean;
  availabilityConstraints: AvailabilityConstraints | null;
  availableFulfillments: GetAvailableOrderFulfillmentResponse[];
  employeeRole: EmployeeRole | null;
  error: ApolloError | null;
  isCommissionsEnabled: boolean;
  isGrossProfitHidden: boolean;
  loaderArgs: OrderEntryMobileLoaderArgs | null;
  loading: boolean;
  onHold: boolean;
  onSelectOrderGuide: (orderGuide: OrderGuideCollectionData) => void;
  orderGuides: OrderGuideCollectionData[];
  refetch: () => void;
  savedSelectedFulfillment: SavedSelectedFulfillment | null;
  saveSelectedFulfillment: (fulfillment: SavedSelectedFulfillment) => void;
  selectedRestaurant: RestaurantListData | undefined;
  showConfidentialFields: boolean;
  timezones: TimeZoneMap;
}

/**
 * Assembles the inputs the phone Order Entry item loaders depend on.
 *
 * @param params.restaurantUUID - Selected restaurant UUID.
 * @param params.supplierUUID - Selected supplier UUID.
 * @returns {@link OrderEntryMobileData}
 */
export function useOrderEntryMobileData({
  restaurantUUID,
  supplierUUID,
}: UseOrderEntryMobileDataParams): OrderEntryMobileData {
  const {
    selectedRestaurant,
    loading: restaurantLoading,
    error: restaurantError,
    refetch: refetchRestaurant,
  } = useSalesPortalSelectedRestaurantLoader(restaurantUUID, supplierUUID);

  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const restaurantTimezone =
    selectedRestaurant?.restaurantTimezone || localTimezone;
  const supplierTimezone =
    selectedRestaurant?.supplierTimezone || localTimezone;

  const { employeeRole } = useSalesPortalEmployeeRole(supplierUUID);

  const { value: additionalOrderValidationRolesConfig } =
    useChatSalesPortalDynamicConfigValue(
      supplierUUID,
      'additional_order_validation_supplier_roles',
      null,
    );
  const additionalOrderValidationRoles = useMemo<EmployeeRole[]>(
    () => additionalOrderValidationRolesConfig ?? [],
    [additionalOrderValidationRolesConfig],
  );

  const {
    availableFulfillments,
    fulfillmentsLoading,
    getAvailabilityConstraints,
    savedSelectedFulfillment,
    saveSelectedFulfillment,
    timezones,
  } = useSalesPortalFulfillments(
    supplierUUID,
    restaurantUUID,
    restaurantTimezone,
    supplierTimezone,
    employeeRole,
    additionalOrderValidationRoles,
  );

  const { salesRepConfig } = useSalesRepConfig(
    supplierUUID,
    selectedRestaurant?.chatUUID ?? null,
  );

  const { value: areSubstitutionsEnabled } =
    useChatSalesPortalDynamicConfigValue(
      supplierUUID,
      'are_substitutions_enabled',
      selectedRestaurant?.chatUUID ?? null,
    );
  const isGrossProfitHidden = useIsGrossProfitHidden(supplierUUID);
  const authenticatedUserUUID = useAuthenticatedUserUUID();
  const isCommissionsEnabled = useIsCommissionsEnabled(
    supplierUUID,
    authenticatedUserUUID,
    selectedRestaurant?.chatUUID ?? null,
  );
  const showConfidentialFields = useDeferredValue(
    useConfidentialVisibilityStore((state) => state.showConfidentialFields),
  );

  const {
    defaultOrderGuide,
    orderGuides,
    loading: orderGuidesLoading,
    error: orderGuidesError,
    refetch: refetchOrderGuides,
  } = useSalesPortalOrderGuideCollectionsLoader(restaurantUUID, supplierUUID);

  const [selectedOrderGuide, setSelectedOrderGuide] =
    useState<OrderGuideCollectionData | null>(null);
  const activeOrderGuide =
    selectedOrderGuide &&
    orderGuides.some((guide) => guide.uuid === selectedOrderGuide.uuid)
      ? selectedOrderGuide
      : defaultOrderGuide;
  // defer the guide switch so the tap responds instantly
  const deferredOrderGuide = useDeferredValue(activeOrderGuide);

  const availabilityConstraints = useMemo(() => {
    if (!selectedRestaurant || !savedSelectedFulfillment?.date) {
      return null;
    }

    return getAvailabilityConstraints(
      savedSelectedFulfillment.type,
      savedSelectedFulfillment.date,
      selectedRestaurant.restaurantTimezone,
      selectedRestaurant.supplierTimezone,
    );
  }, [
    getAvailabilityConstraints,
    savedSelectedFulfillment,
    selectedRestaurant,
  ]);

  const loaderArgs = useMemo<OrderEntryMobileLoaderArgs | null>(() => {
    if (
      !selectedRestaurant ||
      !savedSelectedFulfillment?.date ||
      !deferredOrderGuide
    ) {
      return null;
    }
    return {
      chatUUID: selectedRestaurant.chatUUID,
      deliveryDate: savedSelectedFulfillment.date,
      linkedSupplierCollectionUUID:
        deferredOrderGuide.linkedSupplierCollectionUUID ?? undefined,
      orderGuideUUID: deferredOrderGuide.uuid,
      restaurantTimezone,
      restaurantUUID,
      salesRepConfig,
      savedSelectedFulfillment,
      supplierTimezone,
      supplierUUID,
    };
  }, [
    deferredOrderGuide,
    restaurantTimezone,
    restaurantUUID,
    salesRepConfig,
    savedSelectedFulfillment,
    selectedRestaurant,
    supplierTimezone,
    supplierUUID,
  ]);

  const refetch = useCallback(() => {
    void refetchRestaurant();
    void refetchOrderGuides();
  }, [refetchOrderGuides, refetchRestaurant]);

  return {
    activeOrderGuide,
    additionalOrderValidationRoles,
    areSubstitutionsEnabled: !!areSubstitutionsEnabled,
    availabilityConstraints,
    availableFulfillments,
    employeeRole,
    error: restaurantError ?? orderGuidesError ?? null,
    isCommissionsEnabled,
    isGrossProfitHidden,
    loaderArgs,
    loading: restaurantLoading || fulfillmentsLoading || orderGuidesLoading,
    onHold: selectedRestaurant?.onHold ?? false,
    onSelectOrderGuide: setSelectedOrderGuide,
    orderGuides,
    refetch,
    savedSelectedFulfillment,
    saveSelectedFulfillment,
    selectedRestaurant,
    showConfidentialFields,
    timezones,
  };
}
